import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import LeafletMap from "@/components/LeafletMap";
import { toast } from "sonner";
import { ArrowLeft, Bus, Clock, MapPin, Users, Sparkles, ChevronRight, CheckCircle2, Circle, AlertTriangle, Loader2, Save, RefreshCw } from "lucide-react";

const STATUS_PILL = {
  ON_TIME: { bg: "bg-green-500", text: "text-white", label: "À l'heure" },
  LATE_1_MIN: { bg: "bg-yellow-400", text: "text-zinc-900", label: "Retard léger" },
  LATE_3_MIN: { bg: "bg-orange-500", text: "text-white", label: "Retard important" },
  CRITICAL: { bg: "bg-red-500", text: "text-white", label: "Retard critique" },
};

export default function GtfsTripDetail() {
  const { tripId } = useParams();
  const [detail, setDetail] = useState(null);
  const [live, setLive] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [saving, setSaving] = useState(false);
  const pollRef = useRef(null);

  const loadDetail = useCallback(async () => {
    try {
      const { data } = await api.get(`/gtfs/trips/${tripId}`);
      setDetail(data);
      // The new affectation API is the source of truth; legacy fields kept for back-compat
      setSelectedVehicleId(data.assignment?.vehicle_id || data.vehicle?.id || "");
      setSelectedDriverId(data.assignment?.driver_id || data.driver?.id || "");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  }, [tripId]);

  const loadLive = useCallback(async () => {
    try {
      const { data } = await api.get(`/gtfs/trips/${tripId}/live`);
      setLive(data);
    } catch (e) { /* keep silent */ }
  }, [tripId]);

  const loadOptions = useCallback(async () => {
    try {
      const [{ data: d }, { data: v }] = await Promise.all([api.get("/users?role=driver&active_only=true"), api.get("/vehicles")]);
      setDrivers(d); setVehicles(v);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadDetail(); loadLive(); loadOptions(); }, [loadDetail, loadLive, loadOptions]);
  useEffect(() => {
    pollRef.current = setInterval(loadLive, 8000);
    return () => clearInterval(pollRef.current);
  }, [loadLive]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/gtfs/trips/${tripId}/assign`, {
        vehicle_id: selectedVehicleId || null,
        driver_id: selectedDriverId || null,
      });
      toast.success("Assignation enregistrée");
      loadDetail();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  if (!detail) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm font-mono text-zinc-500">
        <Loader2 className="animate-spin" size={14} /> Chargement de la tournée…
      </div>
    );
  }

  const status = STATUS_PILL[live?.status || "ON_TIME"];
  const shape = detail.shape_coords || [];
  const liveMarker = live?.vehicle ? { lat: live.vehicle.lat, lng: live.vehicle.lon } : null;
  const historyCoords = (live?.history || []).map((h) => [h.lat, h.lon]);
  const stopsForMap = (live?.stops || detail.stops).map((s) => ({
    lat: s.stop_lat, lng: s.stop_lon, address: s.stop_name,
  }));

  return (
    <div className="p-4 lg:p-6" data-testid="gtfs-trip-detail-page">
      <Link to="/admin/gtfs-trips" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 mb-3" data-testid="back-link">
        <ArrowLeft size={11} /> Toutes les tournées
      </Link>

      <header className="border border-zinc-900 bg-white p-4 mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 flex items-center justify-center font-display font-black text-lg border border-zinc-900"
            style={{ background: `#${detail.route.color || "FFC60A"}`, color: detail.route.color === "0A0A0B" ? "#FFC60A" : "#0A0A0B" }}
            data-testid="trip-route-badge">
            {detail.route.route_short_name}
          </div>
          <div>
            <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">Trip · {tripId}</div>
            <div className="font-display font-black text-2xl lg:text-3xl tracking-tighter">{detail.route.route_long_name}</div>
            <div className="text-sm text-zinc-700 mt-1 flex items-center gap-1 flex-wrap">
              <span className="font-mono text-xs">→ {detail.trip_headsign}</span>
              <span className="text-zinc-400">·</span>
              <Clock size={12} className="text-zinc-500" />
              <span className="font-mono text-xs">{detail.departure_time?.slice(0,5)} → {detail.arrival_time?.slice(0,5)}</span>
              <span className="text-zinc-400">·</span>
              <MapPin size={12} className="text-zinc-500" />
              <span className="font-mono text-xs">{detail.stops.length} arrêts</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-900 ${status.bg} ${status.text}`} data-testid="trip-status-pill">
            {status.label}
          </span>
          {live?.delay_minutes > 0 && (
            <div className="font-mono text-sm font-bold text-orange-700" data-testid="trip-delay">+{live.delay_minutes} min</div>
          )}
          <button onClick={loadLive} data-testid="refresh-live"
            className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-1 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-900 hover:text-white">
            <RefreshCw size={11} /> Rafraîchir
          </button>
        </div>
      </header>

      <div className="grid lg:grid-cols-12 gap-0 border border-zinc-900">
        <section className="lg:col-span-8 border-r border-zinc-900">
          <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white text-[10px] font-mono tracking-widest uppercase flex items-center justify-between">
            <span>Itinéraire GPS · Position live</span>
            <span className="text-zinc-400">{shape.length} pts · {live?.history?.length || 0} historique</span>
          </div>
          <LeafletMap
            waypoints={stopsForMap}
            position={liveMarker}
            shapeCoords={shape}
            historyCoords={historyCoords}
            height={520}
            showPath={false}
            testId="trip-detail-map"
          />
          <div className="px-4 py-3 border-t border-zinc-900 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                <span>Progression de la tournée</span>
                <span data-testid="trip-progress">{live?.progress_pct || 0} %</span>
              </div>
              <div className="h-2 bg-zinc-200 border border-zinc-900">
                <div className={`h-full ${(live?.delay_minutes || 0) > 3 ? "bg-orange-500" : "bg-[#FFC60A]"}`}
                  style={{ width: `${live?.progress_pct || 0}%` }} />
              </div>
            </div>
            {live?.eta_next_min !== null && live?.eta_next_min !== undefined && (
              <div className="text-center">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">ETA prochain</div>
                <div className="font-display font-black text-xl tracking-tighter">{live.eta_next_min} min</div>
              </div>
            )}
          </div>
        </section>

        <aside className="lg:col-span-4 max-h-[640px] overflow-y-auto">
          <div className="p-3 border-b border-zinc-900 bg-zinc-900 text-white text-[10px] font-mono tracking-widest uppercase flex items-center justify-between">
            <span>Assignation</span>
            {detail.has_active_affectation ? (
              <span data-testid="affectation-badge-active" className="inline-flex items-center gap-1 bg-green-500 text-white px-1.5 py-0.5 text-[9px] font-bold border border-white">
                <CheckCircle2 size={10} /> Affectation active
              </span>
            ) : (
              <span data-testid="affectation-badge-missing" className="inline-flex items-center gap-1 bg-orange-500 text-white px-1.5 py-0.5 text-[9px] font-bold border border-white">
                <AlertTriangle size={10} /> Aucune
              </span>
            )}
          </div>

          {/* Read-only summary of the active affectation (source of truth) */}
          {detail.has_active_affectation && (
            <div className="p-4 border-b border-zinc-200 bg-[#FFF7D6] text-sm space-y-1.5" data-testid="active-affectation-summary">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-600">Source de vérité · Affectations</div>
              <div className="flex items-center gap-2">
                <Bus size={13} className="text-zinc-600" />
                <span className="font-semibold">{detail.vehicle?.plate || "—"}</span>
                <span className="text-zinc-500 text-xs">· {detail.vehicle?.model || ""}</span>
              </div>
              {detail.vehicle?.navixy_tracker_id && (
                <div className="text-[11px] font-mono text-zinc-700 flex items-center gap-1">
                  {String(detail.vehicle.navixy_tracker_id).startsWith("sim-") ? (
                    <>
                      <span className="px-1 py-0.5 text-[8px] uppercase tracking-widest bg-orange-100 text-orange-800 border border-orange-300">DÉMO</span>
                      <span>Traceur simulé</span>
                    </>
                  ) : (
                    <>
                      <span className="px-1 py-0.5 text-[8px] uppercase tracking-widest bg-green-100 text-green-800 border border-green-300">GPS</span>
                      <span>{detail.vehicle.model || "Traceur GPS lié"}</span>
                    </>
                  )}
                </div>
              )}
              <div className="text-xs">
                <span className="font-mono text-zinc-500 uppercase tracking-widest text-[10px] mr-1">Chauffeur :</span>
                {detail.driver?.name || <span className="text-orange-700 font-mono">⚠ Non assigné</span>}
              </div>
              <div className="text-xs">
                <span className="font-mono text-zinc-500 uppercase tracking-widest text-[10px] mr-1">Badge BLE :</span>
                {detail.bluetooth_tag?.name || detail.bluetooth_tag?.identifier || <span className="text-zinc-400">—</span>}
              </div>
              <div className="text-xs">
                <span className="font-mono text-zinc-500 uppercase tracking-widest text-[10px] mr-1">Statut :</span>
                <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest bg-green-500 text-white border border-zinc-900">
                  {detail.active_affectation?.status || "active"}
                </span>
              </div>
            </div>
          )}

          <div className="p-4 space-y-3">
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">
                Véhicule {detail.has_active_affectation && <span className="text-zinc-400">(modifier l'affectation)</span>}
              </label>
              <select value={selectedVehicleId} onChange={(e) => setSelectedVehicleId(e.target.value)}
                data-testid="assign-vehicle-select"
                className="mt-1 w-full border border-zinc-900 bg-white px-2 py-1.5 text-sm font-mono">
                <option value="">— Aucun —</option>
                {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate} · {v.model} (cap. {v.capacity})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Chauffeur</label>
              <select value={selectedDriverId} onChange={(e) => setSelectedDriverId(e.target.value)}
                data-testid="assign-driver-select"
                className="mt-1 w-full border border-zinc-900 bg-white px-2 py-1.5 text-sm font-mono">
                <option value="">— Aucun —</option>
                {drivers.map((d) => <option key={d.id} value={d.id}>{d.name} · {d.email}</option>)}
              </select>
            </div>
            <button onClick={save} disabled={saving} data-testid="assign-save"
              className="w-full bg-zinc-900 text-[#FFC60A] py-2 text-xs font-bold uppercase tracking-widest inline-flex items-center justify-center gap-1 hover:bg-zinc-800">
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Enregistrer l'assignation
            </button>
            <Link to="/admin/affectations" className="block text-center text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900 underline">
              → Gérer toutes les affectations
            </Link>
          </div>

          {live?.vehicle && (
            <>
              <div className="p-3 border-b border-t border-zinc-900 bg-zinc-100 text-[10px] font-mono tracking-widest uppercase">Véhicule live</div>
              <div className="p-4 text-sm space-y-1">
                <div className="flex items-center gap-2"><Bus size={12} className="text-zinc-500" /> {live.vehicle.label}</div>
                <div className="font-mono text-xs text-zinc-600">{live.vehicle.lat.toFixed(4)}, {live.vehicle.lon.toFixed(4)} · {live.vehicle.speed} km/h</div>
              </div>
            </>
          )}
          {!live?.vehicle && (
            <div className="p-4 text-sm text-zinc-500 border-t border-zinc-900 flex items-center gap-2">
              <AlertTriangle size={14} />
              {detail.has_active_affectation
                ? "Aucune position GPS reçue pour cette tournée actuellement (traceur hors ligne ?)."
                : "Aucun véhicule réel affecté."}
            </div>
          )}
        </aside>
      </div>

      <section className="mt-6 border border-zinc-900" data-testid="stops-progress-section">
        <div className="px-4 py-3 bg-zinc-900 text-white text-[10px] font-mono tracking-widest uppercase flex items-center justify-between">
          <span>Arrêts · Retard par arrêt (théorique GTFS vs estimé)</span>
          <span className="text-zinc-400">{live?.stops?.length || detail.stops.length} arrêts</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 text-zinc-700">
              <tr>
                {["#", "Arrêt", "Prévu", "Estimé", "Retard", "Statut"].map((h) => (
                  <th key={h} className="text-left px-3 py-2 text-[10px] font-mono tracking-widest uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(live?.stops || detail.stops).map((s, i) => {
                const delay = s.delay_min ?? 0;
                const stStatus = s.status || "UPCOMING";
                const Icon = stStatus === "PASSED" ? CheckCircle2 : stStatus === "CURRENT" ? ChevronRight : Circle;
                const iconColor = stStatus === "PASSED" ? "text-green-600" : stStatus === "CURRENT" ? "text-[#FFC60A]" : "text-zinc-300";
                return (
                  <tr key={s.stop_id + "_" + i} className={`border-t border-zinc-200 ${stStatus === "CURRENT" ? "bg-[#FFF7D6]" : ""}`} data-testid={`stop-row-${i}`}>
                    <td className="px-3 py-2 font-mono text-xs">{s.stop_sequence || i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Icon size={14} className={iconColor} />
                        <span className="font-semibold">{s.stop_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{(s.scheduled || s.arrival_time || "").slice(0,5)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{(s.estimated || (s.scheduled || s.arrival_time || "")).slice(0,5)}</td>
                    <td className={`px-3 py-2 font-mono text-xs ${delay > 0 ? "text-orange-700 font-bold" : "text-zinc-500"}`}>
                      {delay > 0 ? `+${delay} min` : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{stStatus}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {live?.history && live.history.length > 0 && (
        <section className="mt-4 border border-zinc-900" data-testid="history-section">
          <div className="px-4 py-3 bg-zinc-900 text-white text-[10px] font-mono tracking-widest uppercase">
            Historique GPS · {live.history.length} points
          </div>
          <div className="p-3 max-h-48 overflow-y-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-zinc-500 text-[10px]">
                  <th className="text-left py-1">Horodatage</th>
                  <th className="text-left py-1">Position</th>
                  <th className="text-left py-1">Vitesse</th>
                </tr>
              </thead>
              <tbody>
                {[...live.history].reverse().slice(0, 30).map((h, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="py-1">{(h.timestamp || "").slice(11, 19)}</td>
                    <td className="py-1">{h.lat?.toFixed(4)}, {h.lon?.toFixed(4)}</td>
                    <td className="py-1">{h.speed} km/h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="mt-4 text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-400 inline-flex items-center gap-1">
        <Sparkles size={11} /> Retards calculés en comparant GTFS théorique vs position Logitrak temps réel
      </div>
    </div>
  );
}
